import { ClipboardDocumentListIcon } from '@heroicons/react/24/outline';
import { isEmpty } from 'radash';
import { useCallback, useState } from 'react';
import { Controller, useController, useWatch } from 'react-hook-form';
import { useShallow } from 'zustand/react/shallow';

import { Combobox } from '@/components/core/Combobox';
import { IconButton } from '@/components/core/IconButton';
import { logger } from '@/services/logger';

import { useAttributeLabelTranslator } from '../hooks/useAttributeLabelTranslator';
import { useCachedAdditionalCategory } from '../hooks/useCachedAdditionalCategory';
import useFocusOnAttribute from '../hooks/useFocusOnAttribute';
import { useIsAttributeReadonly } from '../hooks/useIsAttributeReadonly';
import { useIsHiddenAttribute } from '../hooks/useIsHiddenAttribute';
import { usePlaceholder } from '../hooks/usePlaceholder';
import { useValidationsStore } from '../hooks/useValidationsStore';
import { Layout } from '../Layout/Layout';
import { useAdditionalCategories } from '../queries';
import { EditorComponent, Validation } from '../types';
import AdditionalCategoryTreeDialog from './AdditionalCategoryTreeDialog';

interface AdditionalCategoryComponentProps extends EditorComponent {
    // String specific props go here
}

const AdditionalCategoryComponent = ({
    attribute,
    direction = 'horizontal',
    fieldPath = attribute.name,
    hidden = false,
    readonly = false,
    hideLabel = false,
    gridMode = false,
    isKeyAttribute = false,
    beforeAttributeControl,
    handleUrlChange,
    labelClassName
}: AdditionalCategoryComponentProps) => {

    const validationErrors = useValidationsStore(useShallow(state => state.validationErrors[fieldPath] as Validation[]));
    const validationWarnings = useValidationsStore(useShallow(state => state.validationWarnings[fieldPath] as Validation[]));
    const hasErrors = !isEmpty(validationErrors);
    const hasWarnings = !isEmpty(validationWarnings);

    const attributeLabelTranslator = useAttributeLabelTranslator();
    const attributeLabel = attributeLabelTranslator.translateAttributeLabel(attribute);

    const focusRef = useFocusOnAttribute<HTMLDivElement>(attribute);

    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [inputText, setInputText] = useState<string>();

    hidden = useIsHiddenAttribute(attribute, attribute.name, hidden);

    readonly = useIsAttributeReadonly(attribute, attribute.name, readonly);

    const { field } = useController({
        name: fieldPath,
    });
    const selectedValue = useWatch({
        name: fieldPath,
    });

    const placeholder = usePlaceholder(attribute, readonly);

    const additionalDataModelName = attribute.params?.additionalModule;
    const extension = attribute.params?.extension;

    const { data: searchOptions, isLoading: isLoadingSearchOptions } = useAdditionalCategories({
        variables: {
            dataModel:  additionalDataModelName ?? '',
            extension: extension ?? '',
            leaf: true,
            query: inputText ?? '',
        },
        enabled: inputText !== undefined && inputText?.length >= 3,
        select: options => options.map(option => ({
            value: option.code,
            label: `${option.title} [${option.code}]`
        }))
    });

    const { cachedAdditionalCategory, isLoading: isLoadingSelectedOption } =
        useCachedAdditionalCategory(selectedValue, additionalDataModelName, extension);

    let selectedAdditionalCategory: { value: string; label: string } | undefined = undefined;
    if (selectedValue && cachedAdditionalCategory) {
        selectedAdditionalCategory = {
            value: cachedAdditionalCategory.code,
            label: `${cachedAdditionalCategory.title} [${cachedAdditionalCategory.code}]`
        };
    }

    const handleSelectViaDialog = useCallback(
        (selectedOption: { value: string; label: string }) => {
            field.onChange(selectedOption.value);
            setIsDialogOpen(false);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [fieldPath],
    );

    const handleDialogOpen = () => {
        setIsDialogOpen(true);
    };

    if (hidden) {
        return null;
    }

    if (!additionalDataModelName) {
        logger.error(`AdditionalCategory: Attribute ${attribute.name} has no additional module defined`, attribute);
        return null;
    }

    return (
        <Layout
            attribute={attribute}
            hideLabel={hideLabel}
            direction={direction}
            fieldPath={fieldPath}
            gridMode={gridMode}
            isKeyAttribute={isKeyAttribute}
            beforeAttributeControl={beforeAttributeControl}
            isClearable={!gridMode && !readonly}
            labelClassName={labelClassName}
        >
            <div className="tw-flex tw-flex-row">
                <Controller
                    name={fieldPath}
                    render={({ field }) => (
                        <div className="tw-w-full tw-relative tw-flex tw-items-center">
                            <IconButton
                                className="input tw-px-3 tw-flex tw-h-full tw-items-center tw-bg-gray-100 tw-whitespace-nowrap tw-mb-0.5"
                                data-testid={`button-open-dialog-${attribute.name}`}
                                icon={ClipboardDocumentListIcon}
                                type="button"
                                disabled={readonly}
                                onClick={handleDialogOpen}
                                size="md"
                            />
                            <Combobox
                                id={fieldPath}
                                isDisabled={readonly}
                                placeholder={placeholder}
                                onFocus={handleUrlChange}
                                ref={focusRef}
                                isLoading={isLoadingSelectedOption || isLoadingSearchOptions}
                                options={searchOptions}
                                value={selectedValue === '' ? null : selectedAdditionalCategory}
                                onInputChange={(value) => {
                                    setInputText(value);
                                }}
                                onChange={(newValue) => {
                                    const value = isEmpty(newValue) ? '' : newValue?.value ?? '';
                                    field.onChange(value);
                                }}
                                inputControlClassnames={`${hasErrors ? 'error-input' : hasWarnings ? 'warning-input' : ''} ${gridMode ? 'tw-bg-white tw-px-2' : 'tw-px-3'}`}
                            />
                        </div>
                    )}
                />
                {isDialogOpen && (
                    <AdditionalCategoryTreeDialog
                        attribute={attribute}
                        handleSelect={handleSelectViaDialog}
                        onClose={() => setIsDialogOpen(false)}
                        open={isDialogOpen}
                        selectedValue={selectedValue}
                        title={attributeLabel}
                    />
                )}
            </div>
        </Layout>
    );
};

export { AdditionalCategoryComponent };
